import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { z } from 'zod';

import { useValorAtrasado } from '@/lib/atraso';
import { request } from '@/lib/http';
import { comTransicao } from '@/lib/movimento';
import { type OpcaoPesquisavel } from '@/ui/components/select-pesquisavel';
import {
  type CondicoesRegra,
  condicoesRegraSchema,
  listaRegrasClassificacaoSchema,
  paginaCentrosCustoSchema,
  previaRegraSchema,
  type RegraClassificacao,
  type RegraClassificacaoEntrada,
  regraClassificacaoSchema,
} from '@fiscoai/contracts';

type ListaRegras = z.infer<typeof listaRegrasClassificacaoSchema>;

const chave = (empresaId: string) => ['regras-classificacao', empresaId];
const base = (empresaId: string) => `/empresas/${empresaId}/regras-classificacao`;

/** A reconferência das notas roda em segundo plano: a contagem por regra é relida depois dela. */
export const RECONFERENCIA_MS = 4000;
/** Espera de digitação antes de pedir a prévia ao servidor. */
export const ATRASO_PREVIA_MS = 400;

export function useRegras(empresaId: string) {
  return useQuery({
    queryKey: chave(empresaId),
    queryFn: ({ signal }) =>
      request(base(empresaId), { schema: listaRegrasClassificacaoSchema, signal }),
  });
}

/** Toda escrita relê as regras agora e de novo após a reconferência; as notas abertas também. */
function useAoGravar(empresaId: string) {
  const cliente = useQueryClient();
  return () => {
    const reler = () => {
      void cliente.invalidateQueries({ queryKey: chave(empresaId) });
      void cliente.invalidateQueries({ queryKey: ['documentos'], refetchType: 'none' });
      void cliente.invalidateQueries({ queryKey: ['pendencias'] });
    };
    reler();
    setTimeout(reler, RECONFERENCIA_MS);
  };
}

export function useSalvarRegra(empresaId: string) {
  const aoGravar = useAoGravar(empresaId);
  return useMutation({
    mutationFn: ({ id, ...entrada }: RegraClassificacaoEntrada & { id: string | null }) =>
      request(id ? `${base(empresaId)}/${id}` : base(empresaId), {
        method: id ? 'PUT' : 'POST',
        body: entrada,
        schema: regraClassificacaoSchema,
      }),
    onSuccess: aoGravar,
  });
}

export function useApagarRegra(empresaId: string) {
  const aoGravar = useAoGravar(empresaId);
  return useMutation({
    mutationFn: (id: string) =>
      request(`${base(empresaId)}/${id}`, { method: 'DELETE', schema: z.unknown() }),
    onSuccess: aoGravar,
  });
}

/** A regra como corpo de escrita (para ligar/desligar sem abrir o editor). */
export function entradaDaRegra(regra: RegraClassificacao): RegraClassificacaoEntrada {
  return {
    nome: regra.nome,
    ativa: regra.ativa,
    condicoes: regra.condicoes,
    classificacao: regra.classificacao,
    classificacaoPersonalizadaId: regra.personalizada?.id ?? null,
    centroCustoId: regra.centroCusto?.id ?? null,
  };
}

/**
 * Reordena na hora (a lista troca com View Transition) e grava a ordem; se o servidor recusar,
 * volta como estava.
 */
export function useOrdenarRegras(empresaId: string) {
  const cliente = useQueryClient();
  const aoGravar = useAoGravar(empresaId);
  return useMutation({
    // Uma gravação de ordem por vez, na ordem dos cliques (a tela já trocou na hora).
    mutationKey: ['ordem-regras', empresaId],
    scope: { id: `ordem-regras-${empresaId}` },
    mutationFn: ({ ids }: { ids: string[] }) =>
      request(`${base(empresaId)}/ordem`, {
        method: 'PUT',
        body: { ids },
        schema: listaRegrasClassificacaoSchema,
      }),
    onMutate: async ({ ids }) => {
      await cliente.cancelQueries({ queryKey: chave(empresaId) });
      const antes = cliente.getQueryData<ListaRegras>(chave(empresaId));
      const porId = new Map(antes?.items.map((r) => [r.id, r]));
      const items = ids.flatMap((id, i) => {
        const r = porId.get(id);
        return r ? [{ ...r, ordem: i + 1 }] : [];
      });
      comTransicao(async () => {
        cliente.setQueryData<ListaRegras>(chave(empresaId), { items });
        // O TanStack avisa a tela no tick seguinte; a transição fotografa depois dele.
        await new Promise((pronto) => setTimeout(pronto, 0));
      });
      return { antes };
    },
    onError: (_erro, _vars, contexto) => {
      if (contexto?.antes) cliente.setQueryData(chave(empresaId), contexto.antes);
    },
    // Com outra troca na fila, reler agora desfaria a tela; a última relê.
    onSuccess: () => {
      if (cliente.isMutating({ mutationKey: ['ordem-regras', empresaId] }) <= 1) aoGravar();
    },
  });
}

/**
 * Prévia da regra em edição: espera a digitação assentar e só pergunta com condições válidas.
 * `condicoesValidas` diz se há o que perguntar (sem nenhuma condição, a prévia pede uma).
 */
export function usePreviaRegra(empresaId: string, condicoes: CondicoesRegra, regraId?: string) {
  const texto = useValorAtrasado(JSON.stringify(condicoes), ATRASO_PREVIA_MS);
  const validas = condicoesRegraSchema.safeParse(JSON.parse(texto));
  const consulta = useQuery({
    queryKey: [...chave(empresaId), 'previa', texto, regraId ?? null],
    queryFn: ({ signal }) =>
      request(`${base(empresaId)}/previa`, {
        method: 'POST',
        body: { condicoes: validas.data, ...(regraId ? { regraId } : {}) },
        schema: previaRegraSchema,
        signal,
      }),
    enabled: validas.success,
    placeholderData: (anterior) => anterior,
  });
  return { ...consulta, condicoesValidas: validas.success };
}

/** "FROTA – Frota" para o seletor de centro de custo. */
export const opcaoCentroCusto = (cc: { id: string; codigo: string; nome: string }) => ({
  valor: cc.id,
  rotulo: `${cc.codigo} – ${cc.nome}`,
});

/** Busca remota dos centros de custo ATIVOS da empresa (identidade estável). */
export function useBuscarCentrosCusto(empresaId: string) {
  return useCallback(
    async (termo: string, signal: AbortSignal): Promise<OpcaoPesquisavel[]> => {
      const pagina = await request(`/empresas/${empresaId}/centros-custo`, {
        schema: paginaCentrosCustoSchema,
        query: { q: termo || undefined, ativos: 'true', limit: 20 },
        signal,
      });
      return pagina.items.map((cc) => ({
        ...opcaoCentroCusto({ id: cc.id, codigo: cc.codigo, nome: cc.descricao }),
        detalhe: cc.natureza === 'CUSTO' ? 'Custo' : 'Despesa',
      }));
    },
    [empresaId],
  );
}
